/**
 * Utility functions for managing query parameters in Path To Live
 * Follows Single Responsibility Principle - each function has one clear purpose
 */

/**
 * Creates a new URLSearchParams object preserving existing query params
 * @param {string} currentSearch - Current location.search string
 * @returns {URLSearchParams} New URLSearchParams with existing params preserved
 */
export function createPreservedQueryParams(currentSearch) {
    return new URLSearchParams(currentSearch);
}

/**
 * Updates tab and status in query params while preserving all other params
 * @param {URLSearchParams} urlParams - URLSearchParams object to update
 * @param {string} tab - Tab value to set
 * @param {string|null} status - Status value to set (null to remove)
 */
export function updateTabAndStatus(urlParams, tab, status) {
    urlParams.set('tab', tab);

    if (status) {
        urlParams.set('status', status);
    } else {
        urlParams.delete('status');
    }
}

/**
 * Builds a pathToLive URL with query parameters
 * @param {URLSearchParams} urlParams - URLSearchParams object
 * @returns {string} Complete URL with query string
 */
export function buildPathToLiveUrl(urlParams) {
    const queryString = urlParams.toString();

    return `/pathToLive${queryString ? `?${queryString}` : ''}`;
}

/**
 * Creates a URL with updated tab and status while preserving all other query params
 * @param {string} currentSearch - Current location.search string
 * @param {string} tab - Tab value to set
 * @param {string|null} status - Status value to set (null to remove)
 * @returns {string} Complete URL with updated query params
 */
export function createPathToLiveUrlWithParams(currentSearch, tab, status = null) {
    const urlParams = createPreservedQueryParams(currentSearch);
    updateTabAndStatus(urlParams, tab, status);

    return buildPathToLiveUrl(urlParams);
}

